<template>
  <div class="login-container">
    <div class="login-card">
      <div class="login-header">
        <div class="login-logo">
          <img src="/icon.png" alt="LES PRÉFABRIQUÉS DU FASO Logo" class="logo-image" />
        </div>
        <h1>LES PRÉFABRIQUÉS DU FASO</h1>
        <p class="subtitle">Exclusivement pour le personnel de l'entreprise LES PRÉFABRIQUÉS DU FASO</p>
      </div>

      <form @submit.prevent="handleLogin" class="login-form">
        <div v-if="authError" class="error-message">
          {{ authError }}
        </div>

        <div class="form-group">
          <label for="email" class="form-label">Email</label>
          <input
            id="email"
            v-model.trim="email"
            type="email"
            class="form-input"
            :disabled="isLoading"
            autocomplete="email"
            required
          />
        </div>

        <div class="form-group">
          <label for="password" class="form-label">Mot de passe</label>
          <div class="password-input-wrapper">
            <input
              id="password"
              v-model.trim="password"
              :type="showPassword ? 'text' : 'password'"
              class="form-input"
              :disabled="isLoading"
              autocomplete="current-password"
              required
            />
            <button
              type="button"
              class="password-toggle"
              @click="showPassword = !showPassword"
              :aria-label="showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
            >
              {{ showPassword ? '👁️' : '👁️‍🗨️' }}
            </button>
          </div>
        </div>

        <button
          type="submit"
          class="login-button"
          :disabled="!email || !password || isLoading"
        >
          <span v-if="isLoading">Connexion...</span>
          <span v-else>Se connecter</span>
        </button>
      </form>

      <div class="login-footer">
        <p class="footer-text">
          Pas encore de compte ? <a href="#" class="footer-link" @click.prevent>Contactez votre administrateur</a>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '../composables/useAuth';

const router = useRouter();
const { login, isAuthenticated, loading: authLoading, error: authError } = useAuth();

const email = ref('admin@prefabriques.local');
const password = ref('');
const showPassword = ref(false);

const isLoading = computed(() => authLoading.value);

// Rediriger si déjà connecté
onMounted(() => {
  if (isAuthenticated.value) {
    router.push('/');
  }
});

async function handleLogin() {
  try {
    await login({
      email: email.value,
      password: password.value
    });
    
    // Rediriger vers la page d'accueil après connexion réussie
    router.push('/');
  } catch (err) {
    // L'erreur est déjà gérée dans le composable
    console.error('Erreur de connexion:', err);
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%);
  padding: var(--spacing-lg);
}

.login-card {
  width: 100%;
  max-width: 420px;
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--spacing-2xl) var(--spacing-2xl) var(--spacing-xl);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.login-header {
  text-align: center;
  margin-bottom: var(--spacing-2xl);
}

.login-logo {
  display: flex;
  justify-content: center;
  margin-bottom: var(--spacing-lg);
}

.logo-image {
  width: 64px;
  height: 64px;
  object-fit: contain;
  border-radius: var(--radius-md);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.login-header h1 {
  margin: 0 0 var(--spacing-sm);
  color: var(--color-text-primary);
  font-size: var(--font-size-4xl);
  font-weight: var(--font-weight-bold);
}

.subtitle {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-relaxed);
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.error-message {
  padding: var(--spacing-md);
  background: #fee;
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  color: #c33;
  font-size: var(--font-size-sm);
  text-align: center;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.form-input {
  width: 100%;
  padding: 0.875rem var(--spacing-md);
  border: 2px solid var(--color-border-light);
  border-radius: var(--radius-md);
  font-size: var(--font-size-lg);
  font-family: var(--font-family);
  transition: all var(--transition-base);
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: var(--color-border-focus);
  box-shadow: var(--shadow-focus);
}

.form-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: var(--color-bg-tertiary);
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-toggle {
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-lg);
  transition: color var(--transition-base);
  z-index: 1;
}

.password-toggle:hover {
  color: var(--color-text-primary);
}

.password-toggle:focus {
  outline: 2px solid var(--color-border-focus);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

.login-button {
  width: 100%;
  padding: 0.875rem var(--spacing-lg);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-base);
  margin-top: var(--spacing-sm);
}

.login-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.login-button:active:not(:disabled) {
  transform: translateY(0);
}

.login-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.login-footer {
  margin-top: var(--spacing-xl);
  text-align: center;
}

.footer-text {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: var(--line-height-relaxed);
}

.footer-link {
  color: var(--color-primary);
  text-decoration: none;
  transition: color var(--transition-base);
}

.footer-link:hover {
  color: var(--color-primary-dark);
  text-decoration: underline;
}
</style>
